import { type ReactNode, useId } from 'react'
import {
  Card,
  CardContent,
  CardTitle,
  CardSubtitle,
  CardContentDecoration,
  CardFooterAction,
} from '@/components/Card'
import styles from './ChecklistStatusCard.module.css'
import { Icon } from '@/components/Icon'
import { AutoSkeleton, Skeleton } from '@/components/Skeleton'

export interface ChecklistStatusCardProps {
  children?: ReactNode
  state: 'incomplete' | 'complete'
  onOpenChecklistPress?: () => unknown
  testId?: string
}

/**
 * Card containing the checklist of items and their status.
 */
export function ChecklistStatusCard({
  children,
  state,
  onOpenChecklistPress,
  testId,
}: ChecklistStatusCardProps) {
  return (
    <Card testId={testId}>
      <CardContent>
        <CardContentDecoration>
          <Icon
            name={state === 'incomplete' ? 'alert' : 'tick'}
            variant="surface-background"
          />
        </CardContentDecoration>
        <CardTitle>
          {state === 'incomplete' ? (
            <>There are missing items</>
          ) : (
            <>All items have been provided</>
          )}
        </CardTitle>
        <CardSubtitle>
          {state === 'incomplete' ? (
            <>To avoid unnecessary delays, please complete your checklist.</>
          ) : (
            <>
              We've received all of the requested documentation. Your Pro will
              let you know if more information is needed.
            </>
          )}
        </CardSubtitle>
        {children}
      </CardContent>
      {state === 'incomplete' && (
        <CardFooterAction
          onPress={onOpenChecklistPress}
          label="Complete your checklist"
          testId="checklist-status-card-complete-button"
        />
      )}
    </Card>
  )
}

interface ChecklistStatusCardItemListProps {
  title: string
  children?: ReactNode
  testId?: string
}

/**
 * A list of items.
 */
export function ChecklistStatusCardItemList({
  title,
  children,
  testId,
}: ChecklistStatusCardItemListProps) {
  const titleId = useId()
  return (
    <div data-test={testId}>
      <p id={titleId} className={styles.itemListTitle}>
        {title}
      </p>
      <ul aria-labelledby={titleId} className={styles.itemList}>
        {children}
      </ul>
    </div>
  )
}

interface ChecklistStatusCardListItemProps {
  text?: string
  isCompleted?: boolean
  testId?: string
}

/**
 * Checklist Status Card List Item
 */
export function ChecklistStatusCardListItem({
  isCompleted,
  text,
  testId,
}: ChecklistStatusCardListItemProps) {
  return (
    <li className={styles.item} data-test={testId}>
      <span className={styles.itemIcon}>
        <Icon
          name={isCompleted ? 'done' : 'warning'}
          variant={isCompleted ? 'success' : 'secondary'}
        />
      </span>
      <span>{text}</span>
    </li>
  )
}

/**
 * Checklist Status Card Skeleton
 */
export function ChecklistStatusCardSkeleton() {
  return (
    <Card>
      <CardContent>
        <CardContentDecoration>
          <Icon name="tick" variant="surface-background" skeleton="full" />
        </CardContentDecoration>
        <CardTitle>
          <AutoSkeleton isSkeleton>Placeholder</AutoSkeleton>
        </CardTitle>
        <CardSubtitle>
          <Skeleton width={200} />
          <Skeleton width={240} />
          <Skeleton width={170} />
        </CardSubtitle>
      </CardContent>
    </Card>
  )
}
